import { useEffect, useState, FormEvent } from 'react'
import { GiphyFetch } from '@giphy/js-fetch-api'
import { IGif } from '@giphy/js-types'

import { SelectedImage, SetState } from 'types'
import { reportError } from 'utils/error'
import { REACT_APP_GIPHY_API_KEY } from 'env'

import Preview from './Preview'
import { Loader } from 'components/Loader/Loader'
import { SearchForm } from './SearchForm'

export interface Props {
  stagedImages: SelectedImage[]
  selectedImages: SelectedImage[]
  setSelectedImages: SetState<SelectedImage[]>
  setStagedImages: SetState<SelectedImage[]>
  multiSelect?: boolean
  showSearchIcon?: boolean
}

const giphyApiClient = new GiphyFetch(REACT_APP_GIPHY_API_KEY)
const LIMIT_PER_PAGE = 10

const UploadFromGiphy = ({
  selectedImages,
  stagedImages,
  multiSelect,
  setSelectedImages,
  setStagedImages,
  showSearchIcon = false,
}: Props) => {
  const [query, setQuery] = useState('')
  const [currentOffset, setCurrentOffset] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [querySubmitted, setQuerySubmitted] = useState(false)
  const [canLoadMore, setCanLoadMore] = useState(false)

  const mapResponseToImages = (response: IGif[]) => {
    return response.map((item: IGif) => ({
      url: item.images.original.url,
    }))
  }

  const search = async (nextOffset?: number) => {
    setCanLoadMore(false)

    const offset = nextOffset || currentOffset

    try {
      const {
        data: gifs,
        pagination: { total_count },
      } = await giphyApiClient.search(query, { limit: LIMIT_PER_PAGE, offset })

      const images = mapResponseToImages(gifs)

      setTotalCount(total_count)

      return images
    } catch (error) {
      reportError(error, { severity: 'error' })
    }
  }

  useEffect(() => {
    if (stagedImages.length > 0) return

    const loadRandomPhotos = async () => {
      try {
        const { data: gifs } = await giphyApiClient.trending({ limit: LIMIT_PER_PAGE })
        const images = mapResponseToImages(gifs)
        setStagedImages(images)
      } catch (error) {
        reportError(error, { severity: 'error' })
      }
    }

    loadRandomPhotos()
  })

  const onSubmit = async (e?: FormEvent | KeyboardEvent) => {
    e?.preventDefault()
    if (query.length < 3) return
    setQuerySubmitted(true)

    const photos = await search()
    if (!photos) return

    setStagedImages(photos)
    setCanLoadMore(true)
  }

  const clearQuery = () => {
    setQuery('')
    setStagedImages([])
    setSelectedImages([])
    setQuerySubmitted(false)
  }

  const loadMore = async () => {
    if (!canLoadMore) return

    setCanLoadMore(false)
    const nextOffset = currentOffset + LIMIT_PER_PAGE
    const photos = await search(nextOffset)
    setCurrentOffset(nextOffset)
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    setStagedImages((oldState) => [...oldState, ...photos!])
  }

  useEffect(() => {
    if (canLoadMore || !querySubmitted) return

    const timer = setTimeout(() => {
      setCanLoadMore(true)
    }, 2500)

    return () => clearTimeout(timer)
  }, [canLoadMore, querySubmitted])

  return (
    <>
      <SearchForm
        placeholder="Search GIPHY for photos"
        query={query}
        clearQuery={clearQuery}
        onChange={(e) => setQuery(e.target.value)}
        onSubmit={onSubmit}
        showSearchAffordance
        showIcon={showSearchIcon}
      />

      {stagedImages.length ? (
        <Preview
          stagedImages={stagedImages}
          selectedImages={selectedImages}
          multiSelect={multiSelect}
          setSelectedImages={setSelectedImages}
          loadMore={loadMore}
          hasMore={canLoadMore && currentOffset <= totalCount}
        />
      ) : (
        <Loader />
      )}
    </>
  )
}

export default UploadFromGiphy
